import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { GIFT_CARD_LIMITS, type GiftCardDto, type GiftCardStatus } from '@ecom/contracts';
import { FilterBar } from '@/components/filters';
import { GiftIcon, PlusIcon } from '@/components/icons';
import { KeyValue, ListCard, ListToolbar, LoadMore, Page, Segmented, Sheet, Table, TableScroll, Td, Th, Tr } from '@/components/page';
import { Alert, Button, CopyButton, EmptyState, Field, Input, MetricCard, Pill, TableSkeleton, Textarea, type PillTone } from '@/components/ui';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { count, date, dateTime, money } from '@/lib/format';
import { riyalsToHalalas } from '@/lib/price-input';
import { useGiftCardsStore } from '@/stores/gift-cards.store';

const TONE: Record<GiftCardStatus, PillTone> = { ACTIVE: 'success', REDEEMED: 'neutral', VOID: 'danger' };

/**
 * Gift cards (ADR-082): every card, searched and paged on the server — the
 * last four of a code in a customer's hand finds it — with what the store
 * still owes on them, and the two things staff do: issue, and void.
 */
export function GiftCardsPage() {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { items, nextCursor, total, search, status, summary, loading, error, load, loadMore, clearSaveError } = useGiftCardsStore();
  const [issuing, setIssuing] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const open = items.find((item) => item.id === openId) ?? null;

  useEffect(() => {
    void load();
  }, [load]);

  const issueButton = (
    <Button
      onClick={() => {
        clearSaveError();
        setIssuing(true);
      }}
    >
      <PlusIcon className="size-4" />
      {t('giftCardsIssue')}
    </Button>
  );

  return (
    <Page title={t('giftCards')} subtitle={t('giftCardsSubtitle')} actions={issueButton} fill>
      {summary ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <MetricCard label={t('giftCardsActive')} value={count(summary.active, locale)} icon={<GiftIcon className="size-5" />} />
          <MetricCard
            label={t('giftCardsOutstanding')}
            value={money(summary.outstanding, locale, summary.currency)}
            hint={t('giftCardsOutstandingHint')}
            tone="amber"
            icon={<GiftIcon className="size-5" />}
          />
        </div>
      ) : null}

      <ListCard>
        <ListToolbar>
          <div className="flex flex-wrap items-center gap-3">
            <Segmented
              label={t('status')}
              items={[
                { id: '', label: t('giftCardsAll') },
                { id: 'ACTIVE', label: t('giftCardStatus_ACTIVE') },
                { id: 'REDEEMED', label: t('giftCardStatus_REDEEMED') },
                { id: 'VOID', label: t('giftCardStatus_VOID') },
              ]}
              value={status}
              onChange={(next) => void load({ status: next as typeof status })}
            />
            <div className="min-w-48 flex-1">
              <FilterBar search={search} onSearch={(term) => void load({ search: term })} placeholder={t('giftCardsSearch')} total={total ?? undefined} shown={items.length} />
            </div>
          </div>
        </ListToolbar>

        {error ? (
          <div className="px-4 pt-4">
            <Alert>{error}</Alert>
          </div>
        ) : null}

        {loading && items.length === 0 ? (
          <TableSkeleton columns={5} />
        ) : items.length === 0 ? (
          <EmptyState title={t('giftCardsEmpty')} body={t('giftCardsEmptyHint')} icon={<GiftIcon />} action={issueButton} />
        ) : (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th>{t('giftCardCode')}</Th>
                  <Th>{t('giftCardValue')}</Th>
                  <Th>{t('status')}</Th>
                  <Th>{t('giftCardFor')}</Th>
                  <Th>{t('giftCardIssued')}</Th>
                </tr>
              </thead>
              <tbody>
                {items.map((card) => (
                  <Tr key={card.id} onClick={() => setOpenId(card.id)}>
                    <Td>
                      <code dir="ltr" className="whitespace-nowrap text-sm text-ink">
                        {card.code}
                      </code>
                    </Td>
                    <Td data-numeric className="whitespace-nowrap font-medium text-ink">
                      {money(card.value, locale, card.currency)}
                    </Td>
                    <Td>
                      <Pill tone={TONE[card.status]}>{t(`giftCardStatus_${card.status}`)}</Pill>
                    </Td>
                    <Td className="text-ink-muted">{card.redeemedBy?.name ?? card.recipientName ?? card.recipientEmail ?? '—'}</Td>
                    <Td className="whitespace-nowrap text-ink-muted">{date(card.createdAt, locale)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        )}

        <LoadMore visible={Boolean(nextCursor)} loading={loading} onClick={() => void loadMore()} />
      </ListCard>

      <IssueSheet open={issuing} onClose={() => setIssuing(false)} />
      {open ? <CardSheet card={open} onClose={() => setOpenId(null)} /> : null}
    </Page>
  );
}

function IssueSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { issue, issued, clearIssued, saving, saveError } = useGiftCardsStore();
  const [value, setValue] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [expiresOn, setExpiresOn] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!open) return;
    clearIssued();
    setValue('');
    setQuantity('1');
    setExpiresOn('');
    setRecipientName('');
    setRecipientEmail('');
    setMessage('');
  }, [open, clearIssued]);

  const halalas = riyalsToHalalas(value);
  const many = Number(quantity);
  const ready =
    halalas !== null &&
    halalas >= 100 &&
    halalas <= GIFT_CARD_LIMITS.maxValue &&
    Number.isInteger(many) &&
    many >= 1 &&
    many <= GIFT_CARD_LIMITS.maxBatch &&
    (many === 1 || !recipientEmail.trim());

  async function submit() {
    if (!ready || halalas === null) return;
    await issue({
      value: halalas,
      quantity: many,
      expiresOn: expiresOn || null,
      recipientName: recipientName.trim() || null,
      recipientEmail: recipientEmail.trim() || null,
      message: message.trim() || null,
    });
  }

  const codes = issued?.map((card) => card.code).join('\n') ?? '';

  return (
    <Sheet
      open={open}
      title={issued ? t('giftCardsIssuedTitle', { count: String(issued.length) }) : t('giftCardsIssue')}
      subtitle={issued ? t('giftCardsIssuedHint') : t('giftCardsIssueHint')}
      onClose={onClose}
      footer={
        issued ? (
          <>
            <CopyButton value={codes} label={t('giftCardsCopyAll')} copiedLabel={t('copied')} />
            <Button onClick={onClose}>{t('done')}</Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={saving}>
              {t('cancel')}
            </Button>
            <Button disabled={!ready || saving} onClick={() => void submit()}>
              {t('giftCardsIssue')}
            </Button>
          </>
        )
      }
    >
      {issued ? (
        <ul className="flex flex-col divide-y divide-line rounded-control border border-line">
          {issued.map((card) => (
            <li key={card.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
              <code dir="ltr" className="text-base font-semibold tracking-wider text-ink">
                {card.code}
              </code>
              <span className="flex items-center gap-2 text-sm text-ink-muted">
                {money(card.value, locale, card.currency)}
                <CopyButton value={card.code} label={t('copy')} copiedLabel={t('copied')} />
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('giftCardValueSar')} htmlFor="gift-value" hint={t('giftCardValueHint')}>
              <Input id="gift-value" dir="ltr" inputMode="decimal" autoComplete="off" value={value} onChange={(event) => setValue(event.target.value)} />
            </Field>
            <Field label={t('giftCardQuantity')} htmlFor="gift-quantity" hint={t('giftCardQuantityHint')}>
              <Input id="gift-quantity" dir="ltr" inputMode="numeric" value={quantity} onChange={(event) => setQuantity(event.target.value.replace(/\D/g, ''))} />
            </Field>
          </div>
          <Field label={t('giftCardExpires')} htmlFor="gift-expires" hint={t('giftCardExpiresHint')}>
            <Input id="gift-expires" type="date" dir="ltr" value={expiresOn} onChange={(event) => setExpiresOn(event.target.value)} />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('giftCardRecipientName')} htmlFor="gift-name">
              <Input id="gift-name" maxLength={120} value={recipientName} onChange={(event) => setRecipientName(event.target.value)} />
            </Field>
            <Field label={t('giftCardRecipientEmail')} htmlFor="gift-email" hint={t('giftCardRecipientEmailHint')}>
              <Input id="gift-email" type="email" dir="ltr" value={recipientEmail} onChange={(event) => setRecipientEmail(event.target.value)} />
            </Field>
          </div>
          <Field label={t('giftCardMessage')} htmlFor="gift-message">
            <Textarea id="gift-message" rows={3} maxLength={GIFT_CARD_LIMITS.message} value={message} onChange={(event) => setMessage(event.target.value)} />
          </Field>
          {saveError ? <Alert>{saveError}</Alert> : null}
        </div>
      )}
    </Sheet>
  );
}

function CardSheet({ card, onClose }: { card: GiftCardDto; onClose: () => void }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { voidCard, resend, saving, saveError, clearSaveError } = useGiftCardsStore();
  const [voiding, setVoiding] = useState(false);
  const [reason, setReason] = useState('');
  const [sent, setSent] = useState(false);

  useEffect(() => {
    clearSaveError();
    setVoiding(false);
    setReason('');
    setSent(false);
  }, [card.id, clearSaveError]);

  return (
    <Sheet
      open
      title={card.code}
      subtitle={money(card.value, locale, card.currency)}
      onClose={onClose}
      footer={
        card.status === 'ACTIVE' ? (
          voiding ? (
            <>
              <Button variant="secondary" onClick={() => setVoiding(false)} disabled={saving}>
                {t('cancel')}
              </Button>
              <Button variant="danger" disabled={!reason.trim() || saving} onClick={async () => setVoiding(!(await voidCard(card.id, reason.trim())))}>
                {t('giftCardVoid')}
              </Button>
            </>
          ) : (
            <>
              {card.recipientEmail ? (
                <Button variant="secondary" disabled={saving} onClick={async () => setSent(await resend(card.id))}>
                  {t('giftCardResend')}
                </Button>
              ) : null}
              <Button variant="secondary" className="text-danger" onClick={() => setVoiding(true)}>
                {t('giftCardVoid')}
              </Button>
            </>
          )
        ) : undefined
      }
    >
      <div className="flex flex-col gap-4">
        <dl className="flex flex-col gap-3">
          <KeyValue label={t('status')}>
            <Pill tone={TONE[card.status]}>{t(`giftCardStatus_${card.status}`)}</Pill>
          </KeyValue>
          <KeyValue label={t('giftCardExpires')}>{card.expiresOn ? date(card.expiresOn, locale) : t('giftCardNeverExpires')}</KeyValue>
          {card.recipientName || card.recipientEmail ? (
            <KeyValue label={t('giftCardFor')}>
              {card.recipientName ?? ''}
              {card.recipientEmail ? (
                <span dir="ltr" className="block text-xs text-ink-muted">
                  {card.recipientEmail}
                </span>
              ) : null}
            </KeyValue>
          ) : null}
          {card.message ? <KeyValue label={t('giftCardMessage')}>{card.message}</KeyValue> : null}
          <KeyValue label={t('giftCardIssued')}>
            {dateTime(card.createdAt, locale)}
            {card.issuedBy ? <span className="block text-xs text-ink-muted">{card.issuedBy.name}</span> : null}
            {card.order ? (
              <Link to={`/orders/${card.order.id}`} dir="ltr" className="block text-xs text-brand-700 hover:underline">
                {card.order.number}
              </Link>
            ) : null}
          </KeyValue>
          {card.redeemedBy && card.redeemedAt ? (
            <KeyValue label={t('giftCardRedeemed')}>
              <Link to={`/customers/${card.redeemedBy.id}`} className="text-brand-700 hover:underline">
                {card.redeemedBy.name}
              </Link>
              <span className="block text-xs text-ink-muted">{dateTime(card.redeemedAt, locale)}</span>
            </KeyValue>
          ) : null}
          {card.voidedAt ? (
            <KeyValue label={t('giftCardVoided')}>
              {dateTime(card.voidedAt, locale)}
              {card.voidReason ? <span className="block text-xs text-ink-muted">{card.voidReason}</span> : null}
            </KeyValue>
          ) : null}
        </dl>

        {voiding ? (
          <Field label={t('giftCardVoidReason')} htmlFor="gift-void-reason" hint={t('giftCardVoidHint')}>
            <Textarea id="gift-void-reason" rows={2} maxLength={500} value={reason} onChange={(event) => setReason(event.target.value)} />
          </Field>
        ) : null}
        {sent ? <Alert tone="success">{t('giftCardSent')}</Alert> : null}
        {saveError ? <Alert>{saveError}</Alert> : null}
      </div>
    </Sheet>
  );
}
